import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { SectionHeading, StatusBadge } from "../../components/ui";

interface Tile {
  to: string;
  icon: string;
  title: string;
  description: string;
}

const TILES: Tile[] = [
  {
    to: "/admin/members",
    icon: "👥",
    title: "会員管理",
    description: "会員一覧の検索・ステータス変更、詳細閲覧",
  },
  {
    to: "/admin/events",
    icon: "🗓",
    title: "イベント管理",
    description: "イベントの作成・編集・公開管理",
  },
  {
    to: "/admin/contents",
    icon: "🎥",
    title: "コンテンツ管理",
    description: "動画・記事・PDF・外部リンクの公開",
  },
  {
    to: "/admin/notices",
    icon: "📢",
    title: "お知らせ管理",
    description: "会員へのお知らせ作成とピン留め",
  },
  {
    to: "/admin/hearings",
    icon: "📝",
    title: "ヒアリング",
    description: "初回ヒアリング回答の閲覧・CSV 出力",
  },
  {
    to: "/admin/checkin",
    icon: "📷",
    title: "受付 (QR)",
    description: "イベント当日の QR スキャン受付",
  },
  {
    to: "/admin/audit-logs",
    icon: "📋",
    title: "監査ログ",
    description: "操作履歴の確認",
  },
];

interface QuotaState {
  monthKey: string;
  sentCount: number;
  limit: number;
  softLimit: number;
  remaining: number;
  softRemaining: number;
}
interface QuotaResp {
  quota: QuotaState;
  linkedMemberCount: number;
}

interface MailTestResp {
  config: { mailEnabled: boolean; hasSendgridKey: boolean; fromAddress: string | null; fromName: string | null };
  result: { ok: boolean; skipped: string | null; status: number | null; errorBody: string | null };
}

export function AdminHomePage() {
  const [quota, setQuota] = useState<QuotaResp | null>(null);
  const [mailTo, setMailTo] = useState("");
  const [mailBusy, setMailBusy] = useState(false);
  const [mailResult, setMailResult] = useState<MailTestResp | null>(null);
  const [mailErr, setMailErr] = useState<string | null>(null);

  const runMailTest = async () => {
    setMailBusy(true);
    setMailErr(null);
    setMailResult(null);
    try {
      const r = await api.post<MailTestResp>("/admin/mail-test", { to: mailTo });
      setMailResult(r);
    } catch (e) {
      setMailErr((e as Error).message);
    } finally {
      setMailBusy(false);
    }
  };

  useEffect(() => {
    api
      .get<QuotaResp>("/admin/line/quota")
      .then(setQuota)
      .catch(() => setQuota(null));
  }, []);

  // quota bar 色 (used / limit に応じて段階)
  const barPercent = quota
    ? Math.min(100, Math.round((quota.quota.sentCount / quota.quota.limit) * 100))
    : 0;
  const barTone =
    barPercent >= 95
      ? "bg-red-500"
      : barPercent >= 80
        ? "bg-orange-500"
        : barPercent >= 50
          ? "bg-yellow-500"
          : "bg-tora";

  return (
    <div>
      <SectionHeading
        level={1}
        subtitle="会員・イベント・コンテンツ・お知らせ・受付・監査ログを管理します。"
      >
        管理ダッシュボード
      </SectionHeading>

      {/* メール送信テスト (不達の原因切り分け用) */}
      <section className="card p-5 md:p-6 mb-6">
        <h3 className="text-lg font-bold text-tora-ink mb-2">メール送信テスト</h3>
        <p className="text-xs text-tora-ink/60 mb-3">
          パスワードリセット等のメールが届かない場合、ここで任意のアドレスにテスト送信して原因を確認できます。
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            type="email"
            className="input max-w-xs"
            placeholder="送信先メールアドレス"
            value={mailTo}
            onChange={(e) => setMailTo(e.target.value)}
          />
          <button className="btn-primary" onClick={runMailTest} disabled={mailBusy || !mailTo}>
            {mailBusy ? "送信中…" : "テスト送信"}
          </button>
        </div>
        {mailErr && <p className="text-sm text-red-600 mt-2">{mailErr}</p>}
        {mailResult && (
          <div className="mt-3 text-sm space-y-2">
            <div className="grid grid-cols-[10rem_1fr] gap-y-1 text-xs">
              <span className="text-tora-ink/60">MAIL_ENABLED</span>
              <span className={mailResult.config.mailEnabled ? "text-green-600" : "text-red-600"}>
                {mailResult.config.mailEnabled ? "有効 (true)" : "無効 (false) ← メールが送られません"}
              </span>
              <span className="text-tora-ink/60">SENDGRID_ID</span>
              <span className={mailResult.config.hasSendgridKey ? "text-green-600" : "text-red-600"}>
                {mailResult.config.hasSendgridKey ? "設定済み" : "未設定 ← APIキーがありません"}
              </span>
              <span className="text-tora-ink/60">送信元</span>
              <span>{mailResult.config.fromName} &lt;{mailResult.config.fromAddress}&gt;</span>
            </div>
            <div className={`rounded p-3 ${mailResult.result.ok ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              {mailResult.result.ok ? (
                <p className="text-green-700 font-bold">
                  ✓ 送信リクエスト成功 (status {mailResult.result.status})
                  <br />
                  <span className="font-normal text-xs">
                    届かない場合は、SendGrid 側のドメイン認証 / 受信側の迷惑メール設定を確認してください。
                  </span>
                </p>
              ) : (
                <div className="text-red-700">
                  <p className="font-bold">
                    ✗ 送信失敗
                    {mailResult.result.skipped && `（${mailResult.result.skipped}）`}
                    {mailResult.result.status && ` (status ${mailResult.result.status})`}
                  </p>
                  {mailResult.result.skipped === "missing_key" && (
                    <p className="text-xs mt-1">SENDGRID_ID が未設定です。secret を登録してください。</p>
                  )}
                  {mailResult.result.errorBody && (
                    <pre className="text-[11px] mt-2 whitespace-pre-wrap break-all bg-white/50 p-2 rounded">
                      {mailResult.result.errorBody}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* LINE 送信状況カード (Phase 2 付帯) */}
      {quota && (
        <section className="card p-5 md:p-6 mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h3 className="text-lg font-bold text-tora-ink">
              LINE 送信状況
            </h3>
            <StatusBadge tone="neutral">{quota.quota.monthKey}</StatusBadge>
            <span className="ml-auto text-xs text-tora-ink/60">
              連携会員数:{" "}
              <span className="font-mono font-semibold text-tora-ink">
                {quota.linkedMemberCount}
              </span>{" "}
              名
            </span>
          </div>

          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-bold text-tora-ink font-mono">
              {quota.quota.sentCount.toLocaleString()}
            </span>
            <span className="text-sm text-tora-ink/60">
              / {quota.quota.limit.toLocaleString()} 通 送信済み
            </span>
            <span className="ml-auto text-xs text-tora-ink/60">
              残り{" "}
              <span className="font-mono font-semibold text-tora-ink">
                {quota.quota.remaining.toLocaleString()}
              </span>{" "}
              通
            </span>
          </div>

          <div className="h-2.5 bg-black/5 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${barTone}`}
              style={{ width: `${barPercent}%` }}
            />
          </div>

          {barPercent >= 95 && (
            <p className="mt-2 text-xs text-red-700 font-semibold">
              ⚠ 今月の送信数が上限に近づいています。以降の配信は soft limit で
              blocking される可能性があります。
            </p>
          )}
          {barPercent >= 80 && barPercent < 95 && (
            <p className="mt-2 text-xs text-orange-700">
              今月の送信数が多めです。残数に注意してください。
            </p>
          )}

          <p className="mt-3 text-[11px] text-tora-ink/50">
            soft limit {quota.quota.softLimit.toLocaleString()} 通を超えると
            自動配信は blocking されます。リマインダー設定は{" "}
            <Link to="/admin/events" className="text-tora underline">
              イベント管理
            </Link>{" "}
            から。
          </p>
        </section>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {TILES.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            className="card p-5 md:p-6 group transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tora"
          >
            <div className="flex items-start gap-4">
              <div
                aria-hidden="true"
                className="w-12 h-12 rounded-lg bg-tora/10 flex items-center justify-center text-2xl flex-shrink-0"
              >
                {t.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-tora-ink group-hover:text-tora transition-colors">
                  {t.title}
                </h3>
                <p className="mt-1 text-sm text-tora-ink/70 leading-relaxed">
                  {t.description}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <p className="mt-8 text-xs text-tora-ink/50">
        各画面の集計表示 (KPI) は次期フェーズで実装予定。
      </p>
    </div>
  );
}
